import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useMemo } from 'react';

import { AiAssessedScore } from '../frontendServices/gameAPI';
import { SocketContext } from '../providers/SocketProvider/SocketProvider';
import BackendService from '../services/backendService';
import useAiAssessedScores from './useAiAssessedScores';

/**
 * Pure reducer for the AI assessment stream. Each event the backend emits on
 * `tenant/.../aiassessment:saved` is a single `AiAssessedScore`; this folds
 * the event into the previous array:
 *   - Ignores events with no `responseId` (defensive; backend should never
 *     emit one, but a malformed payload shouldn't poison the cache).
 *   - Appends if the `responseId` is new.
 *   - Replaces in place (same index) if the `responseId` already exists, so
 *     a re-emit from an edit / retry doesn't duplicate the row.
 */
export function accumulateAiAssessedScore(
  previous: AiAssessedScore[] | undefined,
  incoming: AiAssessedScore | undefined | null,
): AiAssessedScore[] | undefined {
  if (!incoming?.responseId) return previous;
  const list = previous ?? [];
  const idx = list.findIndex(s => s.responseId === incoming.responseId);
  if (idx === -1) return [...list, incoming];
  const next = list.slice();
  next[idx] = incoming;
  return next;
}

/**
 * Fold a batch of incoming scores in arrival order. Used by the rAF-batched
 * subscriber to collapse a burst of stream events into one `setQueryData`
 * call. Skips malformed entries (no responseId).
 */
export function accumulateAiAssessedScores(
  previous: AiAssessedScore[] | undefined,
  incoming: ReadonlyArray<AiAssessedScore | undefined | null>,
): AiAssessedScore[] | undefined {
  let next = previous;
  for (const score of incoming) {
    next = accumulateAiAssessedScore(next, score);
  }
  return next;
}

const useListAllAiAssessedScores = ({
  gameId,
  tenantId,
  slideId,
  isUsingSockets,
  setShowMediaIsDisabled,
}: {
  gameId: string;
  tenantId: string;
  slideId?: string;
  isUsingSockets: boolean;
  setShowMediaIsDisabled?: (val: boolean) => void;
}) => {
  const { gameAPI } = useContext(SocketContext);
  const client = useQueryClient();

  const scoresCacheKey = useMemo(() => ['aiAssessedScores', gameId, slideId], [
    gameId,
    slideId,
  ]);

  // Subscribe to the per-response AI assessment stream. The backend emits one
  // event per AiAssessedScore as scoring completes. We buffer the events and
  // flush them to the React Query cache on the next animation frame so a
  // burst of 30 students in a tight window collapses into one `setQueryData`
  // (one chart re-render) instead of 30 — important on lower-powered hosts
  // like Chromebooks. The accumulation rule lives in
  // `accumulateAiAssessedScores` so the batching + dedup logic is
  // unit-testable without a renderHook harness.
  useEffect(() => {
    if (!isUsingSockets || !gameAPI || !slideId) return;

    let pending: AiAssessedScore[] = [];
    let rafId: number | null = null;

    const flush = () => {
      rafId = null;
      if (pending.length === 0) return;
      const batch = pending;
      pending = [];
      client.setQueryData<AiAssessedScore[] | undefined>(scoresCacheKey, previous =>
        accumulateAiAssessedScores(previous, batch),
      );
    };

    const subscription = gameAPI.game
      .onAiFeedbackStream({ gameId, tenantId, slideId })
      .subscribe(score => {
        pending.push(score);
        if (rafId === null) {
          // `useEffect` only runs client-side, so `window.requestAnimationFrame`
          // is guaranteed to be defined here. No SSR fallback needed.
          rafId = window.requestAnimationFrame(flush);
        }
      });

    return () => {
      subscription.unsubscribe();
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      // Drain any pending scores synchronously on teardown so we don't lose
      // events that arrived in the same tick as the unmount.
      if (pending.length > 0) {
        const batch = pending;
        pending = [];
        client.setQueryData<AiAssessedScore[] | undefined>(scoresCacheKey, previous =>
          accumulateAiAssessedScores(previous, batch),
        );
      }
    };
  }, [isUsingSockets, gameAPI, gameId, tenantId, slideId, scoresCacheKey, client]);
  const {
    data,
    isFetching: isFetchingAiAssessedScores,
    refetch: refetchAiAssessedScores,
  } = useQuery({
    queryKey: scoresCacheKey,
    queryFn: async () => {
      if (isUsingSockets) {
        if (!gameAPI || !slideId) return Promise.resolve(undefined);
        const results = await gameAPI.game.listAllAiAssessments({
          gameId,
          slideId,
          tenantId,
        });
        return results.results;
      } else {
        //if if is not during the live game they are viewing the results - don't use sockets
        if (!slideId) return Promise.resolve(undefined);
        const res = await BackendService.instance.game.retrievePublicAiFeedback({
          gameId,
          slideId,
        });
        if (!res) return Promise.resolve(undefined);
        return res.results;
      }
    },
    enabled:
      Boolean(isUsingSockets && gameAPI && slideId) ||
      Boolean(!isUsingSockets && slideId),
    staleTime: isUsingSockets ? 0 : 1000 * 60 * 5, // No staleTime when using sockets to ensure real-time updates
    retry: false,
    refetchOnMount: isUsingSockets, // Refetch when component mounts if using sockets
  });

  const {
    generateAllAiAssessedScores,
    isFetchingAiAssessedScores: isGeneratingNewScores,
    aiAssessedScores: newAiAssessedScores,
  } = useAiAssessedScores({
    gameId,
    slideId: slideId || '',
    tenantId,
    setShowMediaIsDisabled,
  });

  return {
    aiAssessedScores: data || newAiAssessedScores,
    isGeneratingNewScores,
    isFetchingAiAssessedScores,
    refetchAiAssessedScores,
  };
};

export default useListAllAiAssessedScores;
